import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router';
import { Requirement, CostItem, Project } from '../types';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  ClipboardList,
  DollarSign,
  Edit2,
  Plus,
  Search,
  Trash2,
  Link2,
} from 'lucide-react';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import { setProjectRequirements } from '../services/dataService';
import { updateEpic, updateTask } from '../store';
import { normalizeEntityId } from '../utils/entityIds';
import { APP_FIELD_THEME_CLASS, APP_FILTER_LABEL_CLASS } from '../constants/formControls';
import {
  getPriorityColors,
  getRequirementStatusColors,
  type RequirementStatus,
  type TaskPriority,
} from '../constants/colorScheme';
import {
  WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  WIDGET_STATUS_BADGE_SUCCESS_CLASS,
} from '../constants/widgetTheme';

type Priority = TaskPriority;

const EMPTY_FORM = {
  id: '',
  code: '',
  title: '',
  description: '',
  costItemIds: [] as string[],
  priority: '' as Priority | '',
  status: 'Draft' as RequirementStatus,
};

function generateNextCode(requirements: Requirement[], customPrefix?: string): string {
  if (customPrefix) {
    const prefixed = requirements.filter(r => r.code.startsWith(`R_${customPrefix}_`));
    return `R_${customPrefix}_${prefixed.length + 1}`;
  }
  const numbered = requirements
    .map(r => {
      const m = r.code.match(/^R(\d+)$/);
      return m ? parseInt(m[1], 10) : 0;
    })
    .filter(n => n > 0);
  const max = numbered.length > 0 ? Math.max(...numbered) : 0;
  return `R${max + 1}`;
}

export default function Requirements() {
  const { projectId } = useParams<{ projectId: string }>();
  const { state, reload } = useApp();
  const { projects, tasks, epics } = useOrganizationData(state);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RequirementStatus>('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingReq, setEditingReq] = useState<Requirement | null>(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [codePrefix, setCodePrefix] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<Requirement | null>(null);
  const [saving, setSaving] = useState(false);

  const project = useMemo(
    () => projects.find((p: Project) => String(p.id).toLowerCase() === String(projectId).toLowerCase()),
    [projects, projectId]
  );

  const requirements: Requirement[] = useMemo(
    () => project?.requirements ?? [],
    [project]
  );

  const costItems: CostItem[] = useMemo(() => project?.costs ?? [], [project]);

  const filtered = useMemo(() => {
    return requirements.filter(r => {
      const matchesSearch =
        !searchQuery ||
        r.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (r.description ?? '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [requirements, searchQuery, statusFilter]);

  // Count how many tasks/epics reference each requirement
  const reqUsageCounts = useMemo(() => {
    const counts: Record<string, { tasks: number; epics: number }> = {};
    for (const req of requirements) {
      counts[req.id] = { tasks: 0, epics: 0 };
    }
    for (const task of tasks) {
      for (const rid of task.requirementIds ?? []) {
        if (counts[rid]) counts[rid].tasks++;
      }
    }
    for (const epic of epics) {
      for (const rid of (epic as any).requirementIds ?? []) {
        if (counts[rid]) counts[rid].epics++;
      }
    }
    return counts;
  }, [requirements, tasks, epics]);

  const openCreate = () => {
    setEditingReq(null);
    const nextCode = generateNextCode(requirements, codePrefix || undefined);
    // id will be auto-generated by Firestore; user can optionally override
    setFormData({ ...EMPTY_FORM, code: nextCode });
    setShowDialog(true);
  };

  const openEdit = (req: Requirement) => {
    setEditingReq(req);
    setFormData({
      id: req.id,
      code: req.code,
      title: req.title,
      description: req.description ?? '',
      costItemIds: req.costItemIds,
      priority: req.priority ?? '',
      status: req.status,
    });
    setShowDialog(true);
  };

  const toggleCostItem = (id: string) => {
    setFormData(prev => ({
      ...prev,
      costItemIds: prev.costItemIds.includes(id)
        ? prev.costItemIds.filter(c => c !== id)
        : [...prev.costItemIds, id],
    }));
  };

  const handleSave = async () => {
    if (!project || !formData.title.trim() || !formData.code.trim()) return;
    setSaving(true);
    try {
      const now = new Date().toISOString();
      const currentUser = state?.currentUser;
      const existingIds = requirements.map((r) => r.id);
      const existingCodes = requirements.map((r) => r.code.toUpperCase());

      let nextId = normalizeEntityId(formData.id || '');
      if (!nextId && editingReq) {
        nextId = editingReq.id; // Preserve id in edit mode (immutable)
      }
      // Note: In create mode, if no id provided, Firestore will auto-generate one

      const nextCode = formData.code.trim().toUpperCase();
      const duplicateCode = existingCodes.includes(nextCode) && (!editingReq || editingReq.code.toUpperCase() !== nextCode);
      if (duplicateCode) {
        const shouldContinue = window.confirm(
          `Requirement code "${nextCode}" already exists. Click OK to continue anyway, or Cancel to edit it.`
        );
        if (!shouldContinue) {
          setSaving(false);
          return;
        }
      }

      let updated: Requirement[];
      if (editingReq) {
        const previousId = editingReq.id;
        updated = requirements.map(r =>
          r.id === editingReq.id
            ? {
                ...r,
                id: nextId,
                code: nextCode,
                title: formData.title.trim(),
                description: formData.description || undefined,
                costItemIds: formData.costItemIds,
                priority: (formData.priority as Priority) || undefined,
                status: formData.status,
                updatedAt: now,
              }
            : r
        );
        // Note: id is immutable, so no need to update references
      } else {
        const newReq: Requirement = {
          ...(nextId && { id: nextId }), // Only include id if user provided one
          organizationId: project.organizationId,
          projectId: project.id,
          code: nextCode,
          title: formData.title.trim(),
          description: formData.description || undefined,
          costItemIds: formData.costItemIds,
          priority: (formData.priority as Priority) || undefined,
          status: formData.status,
          createdAt: now,
          updatedAt: now,
          createdBy: currentUser?.id ?? '',
          createdByName: currentUser?.name ?? currentUser?.email ?? 'Unknown',
        };
        updated = [...requirements, newReq];
      }
      await setProjectRequirements(project.organizationId, project.id, updated);
      await reload(state?.currentUser?.email ?? '');
      setShowDialog(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!project || !deleteTarget) return;
    const updated = requirements.filter(r => r.id !== deleteTarget.id);
    await setProjectRequirements(project.organizationId, project.id, updated);
    await reload(state?.currentUser?.email ?? '');
    setDeleteTarget(null);
  };

  if (!project) {
    return (
      <div className="p-6 text-center">
        <p className="mb-4 text-muted-foreground">Project not found</p>
        <Link to="/app/projects" className="text-primary hover:text-primary/80">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Requirements</h1>
            <p className="text-sm text-muted-foreground">
              {requirements.length} requirement{requirements.length !== 1 ? 's' : ''} for{' '}
              <span className="font-medium text-foreground">{project.name}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Code prefix (optional)"
            value={codePrefix}
            onChange={e => setCodePrefix(e.target.value)}
            className={`h-9 w-36 text-sm ${APP_FIELD_THEME_CLASS}`}
            title="Custom prefix for next code, e.g. 'Auth' → R_Auth_1"
          />
          <Button onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Requirement
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5">
          <span className={APP_FILTER_LABEL_CLASS}>Search</span>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by code, title, or description..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={`pl-9 ${APP_FIELD_THEME_CLASS}`}
            />
          </div>
        </div>
        <div className="space-y-1.5 sm:w-40">
          <span className={APP_FILTER_LABEL_CLASS}>Status</span>
          <Select
            value={statusFilter}
            onValueChange={v => setStatusFilter(v as typeof statusFilter)}
          >
            <SelectTrigger className={`w-full ${APP_FIELD_THEME_CLASS}`}>
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Implemented">Implemented</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid gap-4 md:grid-cols-3">
        {(['Draft', 'Approved', 'Implemented'] as RequirementStatus[]).map(s => {
          const count = requirements.filter(r => r.status === s).length;
          const statusColors = getRequirementStatusColors(s);
          return (
            <Card key={s} className="border-border shadow-sm">
              <CardContent className="p-4 flex items-center gap-3">
                <Badge variant="outline" className={statusColors.badge}>
                  {s}
                </Badge>
                <span className="text-2xl font-bold text-foreground">{count}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Requirements list */}
      {filtered.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <ClipboardList className="mx-auto mb-3 h-10 w-10 text-muted-foreground/50" />
          <p className="font-medium text-foreground">
            {requirements.length === 0
              ? 'No requirements yet. Add your first one!'
              : 'No requirements match the current filters.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => {
            const usage = reqUsageCounts[req.id] ?? { tasks: 0, epics: 0 };
            const linkedCostItems = costItems.filter(c => req.costItemIds.includes(c.id));
            return (
              <Card
                key={req.id}
                className="border-border shadow-sm transition-shadow hover:shadow-md"
              >
                <CardContent className="p-5">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    {/* Left: code + title + description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className="rounded border border-primary/20 bg-primary/10 px-2 py-0.5 font-mono text-sm font-semibold text-primary">
                          {req.code}
                        </span>
                        <Badge
                          variant="outline"
                          className={getRequirementStatusColors(req.status).badge}
                        >
                          {req.status}
                        </Badge>
                        {req.priority && (
                          <Badge
                            variant="outline"
                            className={getPriorityColors(req.priority).badge}
                          >
                            {req.priority}
                          </Badge>
                        )}
                      </div>
                      <h3 className="text-base font-semibold text-foreground">{req.title}</h3>
                      {req.description && (
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                          {req.description}
                        </p>
                      )}

                      {/* Linked cost items */}
                      {linkedCostItems.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {linkedCostItems.map(ci => (
                            <Badge
                              key={ci.id}
                              variant="outline"
                              className={WIDGET_STATUS_BADGE_SUCCESS_CLASS}
                            >
                              <DollarSign className="h-3 w-3" />
                              {ci.code ? `${ci.code}: ` : ''}
                              {ci.description}
                              <span className="font-medium">
                                ${ci.amount.toLocaleString()}
                              </span>
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Usage stats */}
                      <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
                        {usage.tasks > 0 && (
                          <span className="flex items-center gap-1">
                            <Link2 className="h-3 w-3" />
                            {usage.tasks} task{usage.tasks !== 1 ? 's' : ''}
                          </span>
                        )}
                        {usage.epics > 0 && (
                          <span className="flex items-center gap-1">
                            <Link2 className="h-3 w-3" />
                            {usage.epics} epic{usage.epics !== 1 ? 's' : ''}
                          </span>
                        )}
                        {usage.tasks === 0 && usage.epics === 0 && (
                          <span className="italic">Not yet linked to any tasks or epics</span>
                        )}
                      </div>
                    </div>

                    {/* Right: actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(req)}
                        className="h-8 px-2"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteTarget(req)}
                        className="h-8 px-2 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={open => { if (!open) setShowDialog(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingReq ? 'Edit Requirement' : 'New Requirement'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>ID *</Label>
                <Input
                  value={formData.id}
                  onChange={e => setFormData(p => ({ ...p, id: e.target.value.toUpperCase() }))}
                  placeholder="RQ-1A2B"
                  className={APP_FIELD_THEME_CLASS}
                />
              </div>
              <div className="space-y-1">
                <Label>Code *</Label>
                <Input
                  value={formData.code}
                  onChange={e => setFormData(p => ({ ...p, code: e.target.value }))}
                  placeholder="R1 / R_Auth_1"
                  className={APP_FIELD_THEME_CLASS}
                />
                <p className="text-xs text-muted-foreground">
                  Use R1 format or R_Track_N for grouped requirements
                </p>
              </div>
              <div className="space-y-1">
                <Label>Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={v =>
                    setFormData(p => ({ ...p, status: v as RequirementStatus }))
                  }
                >
                  <SelectTrigger className={APP_FIELD_THEME_CLASS}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Draft">Draft</SelectItem>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Implemented">Implemented</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={e => setFormData(p => ({ ...p, title: e.target.value }))}
                placeholder="Requirement title"
                className={APP_FIELD_THEME_CLASS}
              />
            </div>

            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Detailed description of this requirement..."
                className={`min-h-[80px] ${APP_FIELD_THEME_CLASS}`}
              />
            </div>

            <div className="space-y-1">
              <Label>Priority</Label>
              <Select
                value={formData.priority || 'none'}
                onValueChange={v =>
                  setFormData(p => ({
                    ...p,
                    priority: v === 'none' ? '' : (v as Priority),
                  }))
                }
              >
                <SelectTrigger className={APP_FIELD_THEME_CLASS}>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="P0">P0 – Critical</SelectItem>
                  <SelectItem value="P1">P1 – High</SelectItem>
                  <SelectItem value="P2">P2 – Medium</SelectItem>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cost item linking */}
            {costItems.length > 0 && (
              <div className="space-y-2">
                <Label>Linked Cost Items</Label>
                <div className="max-h-40 space-y-2 overflow-y-auto rounded-md border border-border bg-muted/20 p-3">
                  {costItems.map(ci => (
                    <label
                      key={ci.id}
                      className="flex cursor-pointer items-center gap-2 rounded p-1 transition-colors hover:bg-accent"
                    >
                      <input
                        type="checkbox"
                        checked={formData.costItemIds.includes(ci.id)}
                        onChange={() => toggleCostItem(ci.id)}
                        className="rounded"
                      />
                      <span className="text-sm flex-1 min-w-0">
                        {ci.code && (
                          <span className="mr-1 font-mono text-xs text-muted-foreground">[{ci.code}]</span>
                        )}
                        {ci.description}
                      </span>
                      <span className={`shrink-0 text-sm font-medium ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`}>
                        ${ci.amount.toLocaleString()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            {costItems.length === 0 && (
              <p className="text-xs italic text-muted-foreground">
                No cost items defined for this project yet. Add them in Project Settings.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.title.trim() || !formData.code.trim()}
            >
              {saving ? 'Saving...' : editingReq ? 'Save Changes' : 'Create Requirement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={open => { if (!open) setDeleteTarget(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Requirement</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{deleteTarget?.code}: {deleteTarget?.title}</strong>? This will not remove
              the reference from existing tasks or epics.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
